/**
 * Phase 6.3: ambient music and sound effects, generated in the browser with WebAudio (nothing is downloaded,
 * nothing costs money). The DM picks the mood (set_mood); Hosts can override it. Volume / on-off / sound
 * effects are per device (localStorage). Browsers only allow sound after a click, so the first Play is manual.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { MOOD_LABELS, SCENE_MOODS, type SceneMood } from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";

interface MoodSpec {
  /** Root note (Hz) and scale (semitones). */
  root: number;
  scale: number[];
  /** Average melody notes per second (0 = none). */
  rate: number;
  wave: OscillatorType;
  drone: number;
  /** Background noise: wind, rain, fire crackle, waves, crowd murmur. */
  noise: null | "wind" | "rain" | "fire" | "waves" | "crowd";
  noiseGain: number;
  /** Drum beats per minute (0 = none). */
  drums: number;
  cutoff: number;
  /** Melody loudness. */
  mel: number;
}

const MAJOR = [0, 2, 4, 7, 9, 12, 14, 16];
const MINOR = [0, 3, 5, 7, 10, 12, 15, 17];
const DORIAN = [0, 2, 3, 5, 7, 9, 10, 12];
const PHRYG = [0, 1, 3, 5, 7, 8, 10, 12];
const WHOLE = [0, 2, 4, 6, 8, 10, 12];

export const MOODS: Record<SceneMood, MoodSpec | null> = {
  calm: { root: 220, scale: MAJOR, rate: 0.35, wave: "sine", drone: 0.05, noise: "wind", noiseGain: 0.015, drums: 0, cutoff: 900, mel: 0.06 },
  tavern: { root: 196, scale: DORIAN, rate: 2.2, wave: "triangle", drone: 0.035, noise: "crowd", noiseGain: 0.03, drums: 0, cutoff: 2200, mel: 0.05 },
  town: { root: 262, scale: MAJOR, rate: 1.0, wave: "triangle", drone: 0.03, noise: "crowd", noiseGain: 0.018, drums: 0, cutoff: 1800, mel: 0.045 },
  forest: { root: 247, scale: MAJOR, rate: 0.6, wave: "sine", drone: 0.04, noise: "wind", noiseGain: 0.03, drums: 0, cutoff: 1200, mel: 0.05 },
  dungeon: { root: 110, scale: MINOR, rate: 0.25, wave: "sine", drone: 0.08, noise: "wind", noiseGain: 0.02, drums: 0, cutoff: 500, mel: 0.05 },
  cave: { root: 98, scale: PHRYG, rate: 0.2, wave: "sine", drone: 0.08, noise: "rain", noiseGain: 0.012, drums: 0, cutoff: 420, mel: 0.045 },
  mystery: { root: 147, scale: WHOLE, rate: 0.45, wave: "sine", drone: 0.06, noise: "wind", noiseGain: 0.012, drums: 0, cutoff: 700, mel: 0.05 },
  battle: { root: 110, scale: MINOR, rate: 2.5, wave: "sawtooth", drone: 0.05, noise: null, noiseGain: 0, drums: 132, cutoff: 1400, mel: 0.03 },
  boss: { root: 82, scale: PHRYG, rate: 2.8, wave: "sawtooth", drone: 0.07, noise: "fire", noiseGain: 0.02, drums: 150, cutoff: 1200, mel: 0.03 },
  storm: { root: 123, scale: MINOR, rate: 0.3, wave: "sine", drone: 0.06, noise: "rain", noiseGain: 0.06, drums: 0, cutoff: 600, mel: 0.04 },
  sea: { root: 165, scale: DORIAN, rate: 0.5, wave: "sine", drone: 0.045, noise: "waves", noiseGain: 0.05, drums: 0, cutoff: 900, mel: 0.045 },
  sad: { root: 147, scale: MINOR, rate: 0.35, wave: "sine", drone: 0.05, noise: null, noiseGain: 0, drums: 0, cutoff: 800, mel: 0.06 },
  triumph: { root: 196, scale: MAJOR, rate: 1.6, wave: "triangle", drone: 0.05, noise: null, noiseGain: 0, drums: 96, cutoff: 2500, mel: 0.05 },
  silence: null,
};

interface Prefs { on: boolean; volume: number; sfx: boolean }
const KEY = "dm.ambient";
const loadPrefs = (): Prefs => {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<Prefs>;
    return { on: p.on === true, volume: typeof p.volume === "number" ? Math.min(1, Math.max(0, p.volume)) : 0.5, sfx: p.sfx !== false };
  } catch {
    return { on: false, volume: 0.5, sfx: true };
  }
};

interface Layer { gain: GainNode; stop: () => void }

/** One per page: survives tab changes. */
class AmbientEngine {
  prefs: Prefs = loadPrefs();
  playing = false;
  mood: SceneMood = "calm";
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private layer: Layer | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private listeners = new Set<() => void>();
  private version = 0;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  snapshot = () => this.version;
  private emit() {
    this.version++;
    localStorage.setItem(KEY, JSON.stringify(this.prefs));
    for (const l of this.listeners) l();
  }

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    this.ctx = new C();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.prefs.volume;
    this.master.connect(this.ctx.destination);
    const len = this.ctx.sampleRate * 2;
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return this.ctx;
  }

  /** Must run from a click (browser autoplay rules). */
  async play() {
    const ctx = this.ensure();
    if (!ctx) return;
    await ctx.resume();
    this.playing = true;
    this.prefs.on = true;
    this.startMood(this.mood);
    this.emit();
  }

  pause() {
    this.playing = false;
    this.prefs.on = false;
    this.fadeOut();
    this.emit();
  }

  setVolume(v: number) {
    this.prefs.volume = Math.min(1, Math.max(0, v));
    if (this.ctx && this.master) this.master.gain.setTargetAtTime(this.prefs.volume, this.ctx.currentTime, 0.1);
    this.emit();
  }

  setSfx(on: boolean) {
    this.prefs.sfx = on;
    this.emit();
  }

  setMood(m: SceneMood) {
    if (m === this.mood) return;
    this.mood = m;
    if (this.playing) this.startMood(m);
    this.emit();
  }

  private fadeOut() {
    const old = this.layer;
    this.layer = null;
    if (!old || !this.ctx) return;
    old.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.8);
    setTimeout(old.stop, 4000);
  }

  private startMood(m: SceneMood) {
    this.fadeOut();
    const spec = MOODS[m];
    const ctx = this.ctx;
    if (!spec || !ctx || !this.master || !this.noiseBuf) return;
    const out = ctx.createGain();
    out.gain.value = 0;
    out.gain.setTargetAtTime(1, ctx.currentTime, 1);
    out.connect(this.master);
    const nodes: AudioScheduledSourceNode[] = [];
    const timers: number[] = [];
    // Echo for space.
    const delay = ctx.createDelay(1);
    delay.delayTime.value = 0.38;
    const fb = ctx.createGain();
    fb.gain.value = 0.3;
    delay.connect(fb).connect(delay);
    delay.connect(out);
    // Drone: root and fifth an octave down, slowly breathing through a low-pass filter.
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = spec.cutoff;
    const dg = ctx.createGain();
    dg.gain.value = spec.drone;
    lp.connect(dg).connect(out);
    for (const [ratio, detune] of [[0.5, -4], [0.75, 3], [0.5, 6]] as const) {
      const o = ctx.createOscillator();
      o.type = spec.wave === "sawtooth" ? "sawtooth" : "triangle";
      o.frequency.value = spec.root * ratio;
      o.detune.value = detune;
      o.connect(lp);
      o.start();
      nodes.push(o);
    }
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = spec.cutoff * 0.4;
    lfo.connect(lfoGain).connect(lp.frequency);
    lfo.start();
    nodes.push(lfo);
    // Noise bed.
    if (spec.noise) {
      const n = ctx.createBufferSource();
      n.buffer = this.noiseBuf;
      n.loop = true;
      const f = ctx.createBiquadFilter();
      const g = ctx.createGain();
      g.gain.value = spec.noiseGain;
      if (spec.noise === "wind") { f.type = "bandpass"; f.frequency.value = 500; f.Q.value = 0.8; }
      else if (spec.noise === "rain") { f.type = "highpass"; f.frequency.value = 2500; }
      else if (spec.noise === "crowd") { f.type = "bandpass"; f.frequency.value = 900; f.Q.value = 1.5; }
      else { f.type = "lowpass"; f.frequency.value = 700; }
      n.connect(f).connect(g).connect(out);
      if (spec.noise === "waves" || spec.noise === "wind") {
        const w = ctx.createOscillator();
        w.frequency.value = spec.noise === "waves" ? 0.12 : 0.05;
        const wg = ctx.createGain();
        wg.gain.value = spec.noiseGain * 0.8;
        w.connect(wg).connect(g.gain);
        w.start();
        nodes.push(w);
      }
      n.start();
      nodes.push(n);
      if (spec.noise === "fire") {
        timers.push(window.setInterval(() => Math.random() < 0.5 && this.click(out, 0.04 + Math.random() * 0.05), 120));
      }
    }
    // Melody: sparse notes from the mood's scale.
    if (spec.rate > 0) {
      let step = 0;
      timers.push(window.setInterval(() => {
        if (Math.random() > spec.rate / 4) return;
        step = Math.max(0, Math.min(spec.scale.length - 1, step + Math.round((Math.random() - 0.5) * 4)));
        const f = spec.root * 2 ** (spec.scale[step]! / 12);
        this.note(out, delay, f, spec.wave === "sawtooth" ? "square" : spec.wave, spec.mel, spec.rate > 1.5 ? 0.35 : 1.6);
      }, 250));
    }
    // Drums.
    if (spec.drums > 0) {
      let beat = 0;
      timers.push(window.setInterval(() => {
        beat++;
        this.drum(out, beat % 4 === 1 ? 0.5 : 0.28, beat % 8 === 5 ? 90 : 60);
      }, 60_000 / spec.drums));
    }
    this.layer = {
      gain: out,
      stop: () => {
        for (const t of timers) clearInterval(t);
        for (const n of nodes) { try { n.stop(); } catch { /* already stopped */ } }
        out.disconnect();
        delay.disconnect();
      },
    };
  }

  private note(out: AudioNode, delay: AudioNode, freq: number, wave: OscillatorType, vol: number, len: number) {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = wave;
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    const f = ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = 2400;
    o.connect(f).connect(g);
    g.connect(out);
    g.connect(delay);
    o.start(t);
    o.stop(t + len + 0.05);
  }

  private drum(out: AudioNode, vol: number, base: number) {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.frequency.setValueAtTime(base * 2.2, t);
    o.frequency.exponentialRampToValueAtTime(base * 0.6, t + 0.25);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + 0.4);
  }

  private click(out: AudioNode, vol: number, at = 0, freq = 3000) {
    const ctx = this.ctx!;
    const t = ctx.currentTime + at;
    const n = ctx.createBufferSource();
    n.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = freq;
    f.Q.value = 2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    n.connect(f).connect(g).connect(out);
    n.start(t, Math.random(), 0.06);
  }

  /** Short sound effects (only once sound was started on this page, and if effects are on). */
  sfx(kind: "dice" | "chime" | "image") {
    if (!this.prefs.sfx || !this.ctx || !this.master || this.ctx.state !== "running") return;
    if (kind === "dice") {
      for (let i = 0; i < 6; i++) this.click(this.master, 0.5 - i * 0.06, i * 0.07 + Math.random() * 0.03, 1800 + Math.random() * 2500);
    } else {
      const notes = kind === "chime" ? [784, 988, 1175, 1568] : [523, 659];
      notes.forEach((f, i) => {
        const t = this.ctx!.currentTime + i * 0.09;
        const o = this.ctx!.createOscillator();
        o.type = "sine";
        o.frequency.value = f;
        const g = this.ctx!.createGain();
        g.gain.setValueAtTime(0.15, t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
        o.connect(g).connect(this.master!);
        o.start(t);
        o.stop(t + 1);
      });
    }
  }
}

export const ambient = new AmbientEngine();

const useAmbient = () => {
  useSyncExternalStore(ambient.subscribe, ambient.snapshot);
  return ambient;
};

/** Always mounted inside a campaign: follows the live mood and plays effects for rolls, awards and pictures. */
export function AmbientDriver() {
  const { mood, rolls, awards, imageVersion } = useGame();
  useEffect(() => { ambient.setMood(mood); }, [mood]);
  // Saved "on": start at the first click anywhere (browsers block sound until then).
  useEffect(() => {
    if (!ambient.prefs.on || ambient.playing) return;
    const go = () => void ambient.play();
    window.addEventListener("pointerdown", go, { once: true });
    return () => window.removeEventListener("pointerdown", go);
  }, []);
  const lastRoll = useRef(rolls[0]?.id);
  useEffect(() => {
    if (rolls[0] && rolls[0].id !== lastRoll.current) ambient.sfx("dice");
    lastRoll.current = rolls[0]?.id;
  }, [rolls]);
  const lastAwards = useRef(awards.length);
  useEffect(() => {
    if (awards.length > lastAwards.current) ambient.sfx("chime");
    lastAwards.current = awards.length;
  }, [awards]);
  const lastImage = useRef(imageVersion);
  useEffect(() => {
    if (imageVersion !== lastImage.current) ambient.sfx("image");
    lastImage.current = imageVersion;
  }, [imageVersion]);
  return null;
}

/** The Ambient music box on the Table page. */
export function AmbientBox() {
  const a = useAmbient();
  const { campaign, isHost, mood, fail } = useGame();
  const [busy, setBusy] = useState(false);
  const pick = async (m: SceneMood) => {
    setBusy(true);
    try {
      await api.setMood(campaign.id, m);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel ambient">
      <div className="row">
        <strong>♪ Ambient music:</strong> <span>{MOOD_LABELS[mood]}</span>
        <button onClick={() => (a.playing ? a.pause() : void a.play())} aria-pressed={a.playing}>{a.playing ? "Pause" : "Play"}</button>
        <label className="small">Volume{" "}
          <input type="range" min={0} max={1} step={0.05} value={a.prefs.volume} onChange={(e) => a.setVolume(Number(e.target.value))} aria-label="Ambient volume" />
        </label>
        <label className="small"><input type="checkbox" checked={a.prefs.sfx} onChange={(e) => a.setSfx(e.target.checked)} /> Sound effects</label>
        {isHost && (
          <label className="small">Mood{" "}
            <select value={mood} disabled={busy} onChange={(e) => void pick(e.target.value as SceneMood)}>
              {SCENE_MOODS.map((m) => <option key={m} value={m}>{MOOD_LABELS[m]}</option>)}
            </select>
          </label>
        )}
      </div>
      <p className="muted small">Made in your browser to match the scene (free; plays on this website, not in Discord). The DM changes the mood as the story moves{isHost ? "; you can override it here" : ""}.</p>
    </section>
  );
}

/** "DM is thinking…": pulsing dots shown to everyone at the table while the DM works on a reply. */
export function DmThinking() {
  const { dmThinking } = useGame();
  if (!dmThinking) return null;
  return (
    <div className="dm-thinking" role="status" aria-live="polite">
      <span className="dots" aria-hidden="true"><i /><i /><i /></span>
      DM is thinking{dmThinking.for ? ` (answering ${dmThinking.for})` : ""}…
    </div>
  );
}
